import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  CELSIUS_ID, FAHRENHEIT_ID, SPEED_FEET, SPEED_METERS,
} from '@avtechsoftware/ra-javascript-utilities/src/utilities/channels/ChannelValueRender';
import ChannelKey from '@avtechsoftware/ra-javascript-utilities/src/classes/ChannelKey';
import getChannelValueWithUnitsFromChannelKeyObjectString
  from '../../../common/ChannelValueWithUnitsFromChannelKeyObject/getChannelValueWithUnitsFromChannelKeyObjectString';
import dashboardApi from '../../services/dashboardApi';
import DashboardHeatIndex from './DashboardHeatIndex';

// Hook that loads whatever data this widget needs.
// DashboardWidget will call this and show a skeleton while isLoading is true.
export const useHeatIndexGaugeWidgetData = (context = {}, options = {}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState(null);
  const [isDefault, setIsDefault] = useState(false);

  const { min = 0, max = 180, channelId = null } = context;
  const { widgetId, refreshTick = 0, isEditMode } = options;

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);

    // No channel configured yet — this is the "default" state. Report it up
    // so DashboardWidget can render the Default component from the registry.
    if (channelId === null || channelId === '') {
      setData(null);
      setIsDefault(true);
      setIsLoading(false);
      return undefined;
    }

    dashboardApi.heatIndex.getChannel(widgetId)
      .then((response) => {
        if (cancelled) return;
        const body = dashboardApi.unwrapBody(response);
        const channel = body && body.channel;
        if (channel) {
          setData({
            rawValue: channel.lastSampleValue,
            channelKey: channel.channelKey,
            customUnit: channel.customUnit || null,
            channelUnit: channel.unit || null,
            min,
            max,
          });
          setIsDefault(false);
        } else {
          setData(null);
          setIsDefault(true);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setData(null);
        setIsDefault(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [widgetId, min, max, refreshTick, isEditMode]);

  return { isLoading, data, isDefault };
};

// Splits a formatted "72.5°F" style string into its numeric value and unit
// portions so the gauge can render the number and unit separately. Mirrors
// the approach used by ChannelWidgetView.
const splitValueAndUnit = (formatted) => {
  if (formatted == null) return { value: null, unit: '' };
  const str = String(formatted).trim();
  const match = str.match(/^([+-]?[\d.,]+)\s*°?\s*(.*)$/);
  if (!match) return { value: null, unit: '' };
  return { value: parseFloat(match[1]), unit: match[2].trim() };
};

const HeatIndexGaugeWidgetView = ({ data, userPreferences }) => {
  if (!data) {
    return <div className="text-center text-muted">No data available</div>;
  }

  const {
    rawValue, channelKey, customUnit, channelUnit, min, max,
  } = data;

  const temperatureScale = (userPreferences && userPreferences.temperatureScale) || CELSIUS_ID;

  // Route the raw sample through the shared formatter so the value honors the
  // user's temperature-scale preference (same pattern as ChannelWidgetView /
  // the alert list cells).
  let value = parseFloat(rawValue);
  let units = channelUnit || (temperatureScale === FAHRENHEIT_ID ? 'F' : 'C');

  if (channelKey != null && rawValue != null) {
    const cko = new ChannelKey(channelKey);
    const formatted = getChannelValueWithUnitsFromChannelKeyObjectString(
      temperatureScale,
      cko,
      String(rawValue),
      customUnit,
    );
    const split = splitValueAndUnit(formatted);
    if (split.value != null && Number.isFinite(split.value)) {
      value = split.value;
    }
    if (split.unit) {
      units = split.unit;
    }
  }

  return (
    <div className="h-100 d-flex flex-column py-2 px-2">
      <div
        className="flex-grow-1 d-flex justify-content-center align-items-center"
        style={{ minHeight: 0 }}
      >
        <DashboardHeatIndex
          value={value}
          units={units}
          min={min}
          max={max}
        />
      </div>
    </div>
  );
};

HeatIndexGaugeWidgetView.propTypes = {
  data: PropTypes.shape({
    rawValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    channelKey: PropTypes.string,
    customUnit: PropTypes.string,
    channelUnit: PropTypes.string,
    min: PropTypes.number,
    max: PropTypes.number,
  }),
  userPreferences: PropTypes.shape({
    temperatureScale: PropTypes.oneOf([CELSIUS_ID, FAHRENHEIT_ID]),
    airSpeedScale: PropTypes.oneOf([SPEED_METERS, SPEED_FEET]),
    timezone: PropTypes.string,
  }),
};

HeatIndexGaugeWidgetView.defaultProps = {
  data: null,
  userPreferences: {
    temperatureScale: CELSIUS_ID,
    airSpeedScale: SPEED_METERS,
  },
};

export default HeatIndexGaugeWidgetView;
